import { Project, ProrataInformation } from '@gameonly/core';
import {
  Alert,
  DialogActions,
  FormControl,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { Form, Formik } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { getParameterFromId } from '../project/utils';
import { WhiteButton } from '../ui/Button';
import LoadingButton from '../ui/LoadingButton';

interface ProrataConfigurationProps {
  onClose: () => void;
  onSave: (prorataInformation: ProrataInformation) => void;
  loading: boolean;
  project: Project;
}

type WorkloadType = 'etp' | 'mh';

type WorkLoadPossibility = {
  type: string;
  value: number;
};

type WorkloadChoice = 'ETP' | 'mois.homme'; // possible values of radio button parameter in model

type WorkloadPossibilities = {
  [key in WorkloadChoice]: { type: WorkloadType; parameterId: string };
};

const workloadMapping: WorkloadPossibilities = {
  ETP: { type: 'etp', parameterId: 'ETP_employes' },
  'mois.homme': { type: 'mh', parameterId: 'moishomme_employes' },
};

const getWorkload = (project: Project): WorkLoadPossibility | null => {
  const workLoadChoice = getParameterFromId(project.sectors, 'ETP_moishomme')
    ?.value as WorkloadChoice;

  if (!workLoadChoice || !workloadMapping[workLoadChoice]) return null;

  const { type, parameterId } = workloadMapping[workLoadChoice];
  const value =
    (getParameterFromId(project.sectors, parameterId)?.value as number) || 0;

  return { type, value };
};

const ProrataConfiguration = ({
  onSave,
  onClose,
  loading,
  project,
}: ProrataConfigurationProps) => {
  const { t } = useTranslation(['cnc-reports', 'common', 'project']);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const workload = getWorkload(project);

  if (!workload) return null;

  const initialValues = {
    prorataType: 'percentage',
    prorataValue: '',
  };

  const getValidationSchema = () =>
    Yup.object().shape({
      prorataValue: Yup.number()
        .typeError(t('create-modal-internal.value-must-be-number'))
        .positive(t('create-modal-internal.value-positive'))
        .nullable(),
    });

  const handleValidate = async (values: typeof initialValues) => {
    const { prorataType, prorataValue } = values;
    const prorata =
      prorataType === 'percentage'
        ? Number(prorataValue) / 100
        : Number(prorataValue) / workload.value;
    if (prorata > 1 || !prorata) {
      setErrorMessage(
        prorataType === 'percentage'
          ? t('create-modal-internal.percentage-limit-error')
          : t(`create-modal-internal.${workload.type}-limit-error`),
      );
      return;
    }
    const prorataInformation = {
      prorata,
      projectSourceName: `${project.name} - ${(prorata * 100).toFixed()}%`,
    };
    await onSave(prorataInformation);
    onClose();
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={getValidationSchema()}
      onSubmit={handleValidate}
    >
      {({ values, setFieldValue }) => {
        const calculatedPercentage =
          values.prorataType === 'percentage'
            ? values.prorataValue
            : ((Number(values.prorataValue) / workload.value) * 100).toFixed(0);

        const calculatedWorkloadValue =
          values.prorataType === 'percentage'
            ? ((workload.value * Number(values.prorataValue)) / 100).toFixed(0)
            : values.prorataValue;

        return (
          <Form>
            {errorMessage && (
              <Alert
                severity="error"
                onClose={() => setErrorMessage(null)}
                sx={{ mb: 2 }}
              >
                {errorMessage}
              </Alert>
            )}

            <FormControl component="fieldset" sx={{ mt: 2 }}>
              <RadioGroup
                row
                value={values.prorataType}
                onChange={(e) => {
                  setFieldValue('prorataType', e.target.value);
                  setFieldValue('prorataValue', '');
                }}
              >
                <FormControlLabel
                  value="percentage"
                  control={<Radio />}
                  label={t('create-modal-internal.percentage-local')}
                />
                <FormControlLabel
                  value="number"
                  control={<Radio />}
                  label={t(`create-modal-internal.number-${workload.type}`)}
                />
              </RadioGroup>
            </FormControl>

            <TextField
              label={
                values.prorataType === 'percentage'
                  ? t('create-modal-internal.percentage-local')
                  : t(`create-modal-internal.number-${workload.type}`)
              }
              type="number"
              fullWidth
              margin="normal"
              value={values.prorataValue}
              onChange={(e) =>
                setFieldValue('prorataValue', Number(e.target.value) || '')
              }
            />

            <TableContainer component={Paper} sx={{ mt: 3 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'var(--backgroundColor)' }}>
                    <TableCell>
                      {t(
                        `create-modal-internal.prorata-table-${workload.type}-selected`,
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {t('create-modal-internal.percentage-local')}
                    </TableCell>
                    <TableCell align="right">
                      {t(`create-modal-internal.number-${workload.type}`)}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>{workload.value}</TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        backgroundColor:
                          values.prorataType === 'percentage'
                            ? 'var(--color-primary-light)'
                            : 'inherit',
                      }}
                    >
                      {calculatedPercentage}%
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        backgroundColor:
                          values.prorataType === 'number'
                            ? 'var(--color-primary-light)'
                            : 'inherit',
                      }}
                    >
                      {calculatedWorkloadValue}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            <DialogActions>
              <WhiteButton onClick={onClose}>{t('common:cancel')}</WhiteButton>
              <LoadingButton type="submit" color="primary" loading={loading}>
                {t('common:validate')}
              </LoadingButton>
            </DialogActions>
          </Form>
        );
      }}
    </Formik>
  );
};

export default ProrataConfiguration;
